/**
 * Real-Time Chat History Component
 * 
 * Enhanced version of ChatHistory with real-time features using our built APIs:
 * - WebSocket integration for live updates
 * - Message status tracking (sent, delivered, read)
 * - Typing indicators with animations
 * - Message reactions system
 * - Agent presence indicators
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import * as Types from '../../../types'
type EnhancedChatMessage = Types.EnhancedChatMessage
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { 
  User,
  Bot,
  Clock,
  Target,
  Zap,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Heart,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  Lock,
  FileText,
  Sparkles
} from 'lucide-react'
// Removed old realTimeConversationsService - now using websocketService
import { api } from '../../../services/api'
import { websocketService } from '../../../services/websocket'
import { cn } from '../../../utils/cn'
import type { WebSocketMessage } from '../../../services/websocket'
import { logger } from '../../../utils/logger'
import { ModernChatInput } from '../../ui/ModernChatInput'
import { useAuth } from '../../../hooks/useAuth'
import { formatMessageTime } from '../../../utils/timeUtils'
import DOMPurify from 'dompurify'
import '../../../styles/code-highlighting.css'

// 🎨 Add smooth animations for message entrance
const animationStyles = `
  @keyframes slideUpAndFade {
    0% {
      opacity: 0;
      transform: translateY(15px) scale(0.98);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @keyframes newMessageBounce {
    0% {
      opacity: 0;
      transform: translateY(20px) scale(0.98);
    }
    60% {
      opacity: 1;
      transform: translateY(-2px) scale(1.005);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  .animate-slideUp {
    animation: slideUpAndFade 0.3s ease-out forwards;
  }
  
  .animate-newMessage {
    animation: newMessageBounce 0.35s cubic-bezier(0.34, 1.26, 0.64, 1) forwards;
  }
  
  .message-group {
    transition: all 0.2s ease-out;
  }
  
  .message-bubble-in-group {
    transition: all 0.15s ease-out;
    -webkit-tap-highlight-color: transparent !important;
    -webkit-touch-callout: none !important;
    -webkit-focus-ring-color: transparent !important;
    outline: none !important;
  }
  
`

/**
 * Format message content with code highlighting and safe HTML rendering
 */
function formatMessageContent(content: string): string {
  // Handle URLs in parentheses first
  let processedContent = content.replace(
    /\((https?:\/\/[^)]+)\)/gi,
    '(<a href="$1" target="_blank" rel="noopener noreferrer" class="text-white hover:text-blue-800 underline">$1</a>)'
  )

  // Split content preserving HTML elements (code blocks, inline code, and links)
  const parts = processedContent.split(/(<pre><code[^>]*>[\s\S]*?<\/code><\/pre>|<code[^>]*>.*?<\/code>|<a[^>]*>.*?<\/a>)/)
  
  const processedParts = parts.map(part => {
    if (part.match(/^<pre><code/)) {
      // Handle code blocks - add proper styling classes
      const codeMatch = part.match(/<pre><code class="language-([^"]*)">([\s\S]*?)<\/code><\/pre>/)
      if (codeMatch) {
        const language = codeMatch[1]
        const code = codeMatch[2]
        return `<div class="admin-code-block">
                  <div class="admin-code-header">
                    <span class="admin-code-language">${language}</span>
                    <button 
                      class="admin-code-copy" 
                      onclick="navigator.clipboard.writeText(this.parentElement.parentElement.querySelector('code').textContent)"
                      title="Copy code"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                      </svg>
                    </button>
                  </div>
                  <pre><code class="language-${language}">${escapeHtml(code)}</code></pre>
                </div>`
      }
      return part
    } else if (part.match(/^<code class="inline-code"/)) {
      // Handle inline code
      const codeMatch = part.match(/<code class="inline-code">(.*?)<\/code>/)
      if (codeMatch) {
        return `<code class="admin-inline-code">${escapeHtml(codeMatch[1])}</code>`
      }
      return part
    } else if (part.match(/^<a/)) {
      return part // Keep links as-is
    } else {
      return escapeHtml(part) // Escape text parts only
    }
  })

  // Sanitize the final HTML
  return DOMPurify.sanitize(processedParts.join(''), {
    ALLOWED_TAGS: ['pre', 'code', 'div', 'span', 'button', 'svg', 'rect', 'path', 'a'],
    ALLOWED_ATTR: ['class', 'onclick', 'title', 'href', 'target', 'rel', 'width', 'height', 'viewBox', 'fill', 'stroke', 'stroke-width', 'rx', 'ry', 'd', 'x', 'y'],
    ALLOW_DATA_ATTR: false
  })
}

/**
 * Escape HTML characters
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Clean content for copying - remove HTML and decode entities
 */
function cleanContentForCopy(content: string): string {
  // Create a temporary element to decode HTML entities and strip tags
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = content
  
  // Get text content which automatically strips HTML tags and decodes entities
  let cleanText = tempDiv.textContent || tempDiv.innerText || ''
  
  // Clean up extra whitespace and normalize line breaks
  cleanText = cleanText
    .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
    .replace(/\n\s*\n/g, '\n') // Remove excessive line breaks
    .trim()
  
  return cleanText
}

// Inject styles into the document
if (typeof document !== 'undefined' && !document.getElementById('chat-animations')) {
  const styleElement = document.createElement('style')
  styleElement.id = 'chat-animations'
  styleElement.textContent = animationStyles
  document.head.appendChild(styleElement)
}

export interface RealTimeChatHistoryProps {
  /** Array of messages to display */
  messages: EnhancedChatMessage[]
  /** Conversation ID for real-time updates */
  conversationId: string
  /** Chatbot ID for sending messages */
  chatbotId: string
  /** Show detailed AI metadata */
  showMetadata?: boolean
  /** Auto-scroll to bottom */
  autoScroll?: boolean
  /** Custom CSS classes */
  className?: string
  /** Callback to refresh conversation data after sending messages */
  onMessageSent?: () => void
  /** Loading state for messages */
  isLoading?: boolean
  /** Error state for message loading */
  error?: string | null
  /** Input value for controlled input */
  inputValue?: string
  /** Callback to set input value */
  setInputValue?: (value: string) => void
  /** Whether note mode is active */
  isNoteMode?: boolean
  /** Callback to toggle note mode */
  onToggleNoteMode?: () => void
  /** Input placeholder text */
  placeholder?: string
  /** Ref to expose the send message function */
  sendMessageRef?: React.MutableRefObject<((message: string) => void) | null>
}

interface MessageStatus {
  messageId: string
  status: 'sent' | 'delivered' | 'read' | 'failed'
  timestamp: string
}

interface MessageReaction {
  messageId: string
  emoji: string
  userId: string
  userName: string
  timestamp: string
}

interface TypingUser {
  userId: string
  userName: string
  isTyping: boolean
  lastUpdate: string
}


/**
 * Message grouping interface and logic for modern chat UI
 */
interface MessageGroup {
  id: string
  sender: string
  role: string
  messages: EnhancedChatMessage[]
  startTime: string
  endTime: string
  showAvatar: boolean
  showSenderInfo: boolean
  showTimestamp: boolean
  isRightAligned: boolean
}

/**
 * Group consecutive messages from the same sender
 */
function groupMessages(messages: EnhancedChatMessage[]): MessageGroup[] {
  if (!messages.length) return []
  
  const groups: MessageGroup[] = []
  let currentGroup: MessageGroup | null = null
  let lastSenderRole: string | null = null
  
  messages.forEach((message, index) => {
    const senderId = `${message.role}-${message.is_internal_note ? 'note' : 'regular'}`
    const messageTime = new Date(message.timestamp)
    const previousMessage = index > 0 ? messages[index - 1] : null
    const previousMessageTime = previousMessage ? new Date(previousMessage.timestamp) : null
    
    // Check if we need to start a new group
    const isNewSender = lastSenderRole !== senderId
    const isTimeDifferent = previousMessageTime && 
      (messageTime.getTime() - previousMessageTime.getTime() > 5 * 60 * 1000) // 5 minutes
    const isInternalNoteChange = previousMessage && 
      message.is_internal_note !== previousMessage.is_internal_note
    
    // Debug checks for role types
    const isUser = message.role === 'user'
    // const isVisitor = message.role === 'visitor' // This should never happen in our system
    const isAgent = message.role === 'agent' || message.role === 'assistant'
    const isSystem = message.role === 'system'
    
    if (isNewSender || isTimeDifferent || isInternalNoteChange || !currentGroup) {
      // Finalize previous group if it exists
      if (currentGroup) {
        if (previousMessage) {
          currentGroup.endTime = previousMessage.timestamp
        }
        groups.push(currentGroup)
      }
      
      // Create a new group
      currentGroup = {
        id: `group-${message.id}`,
        sender: senderId,
        role: message.role,
        messages: [message],
        startTime: message.timestamp,
        endTime: message.timestamp,
        showAvatar: true,
        showSenderInfo: true,
        showTimestamp: true,
        isRightAligned: isAgent || isSystem // Agent and system messages are right-aligned
      }
      
      lastSenderRole = senderId
    } else {
      // Add to existing group
      if (currentGroup) { // Add null check to satisfy TypeScript
        currentGroup.messages.push(message)
        currentGroup.endTime = message.timestamp
      }
    }
  })
  
  // Add the last group if it exists
  if (currentGroup) {
    groups.push(currentGroup)
  }
  
  return groups
}

/**
 * Get message status icon
 */
function getStatusIcon(status: string) {
  switch (status) {
    case 'sent': return '✓'
    case 'delivered': return '✓✓'
    case 'read': return <span className="text-blue-500">✓✓</span>
    case 'failed': return '❌'
    default: return ''
  }
}

/**
 * Message Group Component - Modern WhatsApp/iMessage style grouping
 */
// Message Group Component - Modern WhatsApp/iMessage style grouping
const MessageGroupComponent = React.memo(function MessageGroupComponent({ 
  group, 
  showMetadata,
  messageStatuses,
  messageReactions,
  newMessageIds
}: { 
  group: MessageGroup
  showMetadata: boolean
  messageStatuses: Record<string, MessageStatus>
  messageReactions: Record<string, MessageReaction[]>
  newMessageIds: Set<string>
}) {
  const isUser = group.role === 'user'
  const isAgent = group.role === 'agent' 
  const isAssistant = group.role === 'assistant'
  const isInternalNote = group.sender.includes('note')
  const isRightAligned = group.isRightAligned
  
  // Check if any message in the group is new
  const hasNewMessage = group.messages.some(msg => newMessageIds.has(msg.id))
  
  return (
    <div className={`flex ${isRightAligned ? 'justify-end' : 'justify-start'} mb-6 message-group ${hasNewMessage ? 'animate-newMessage' : ''}`}>
      <div className={`max-w-[85%] ${isRightAligned ? 'order-2' : 'order-1'}`}>
        
        {/* Messages in Group - Tightly connected */}
        <div className="space-y-0.5">
          {group.messages.map((message, index) => {
            // 🔧 FIX: Use stable key that doesn't change when optimistic becomes realtime
            // This prevents React from re-rendering all messages when IDs change
            const stableKey = message.id.startsWith('temp-') 
              ? `${message.timestamp}-${message.content?.substring(0, 20)}-${index}`
              : `${message.id}-grouped`
            
            return (
              <CleanMessageBubble
                key={stableKey}
                message={message}
                showMetadata={showMetadata}
                messageStatus={messageStatuses[message.id]}
                reactions={messageReactions[message.id] || []}
                isFirstInGroup={index === 0}
                isLastInGroup={index === group.messages.length - 1}
                isRightAligned={isRightAligned}
                isNewMessage={newMessageIds.has(message.id)}
              />
            )
          })}
        </div>
        
        {/* Avatar, Agent Name, and Timestamp - Only show when needed */}
        {group.showTimestamp && (
          <div className={`flex items-center gap-2 mt-2 ${isRightAligned ? 'justify-end' : 'justify-start'}`}>
            {!isRightAligned && group.showAvatar && (
              <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                {isUser ? (
                  <User className="w-3 h-3 text-white" />
                ) : (
                  <Bot className="w-3 h-3 text-white" />
                )}
              </div>
            )}
            
            <div className={`flex items-center gap-2 text-xs text-gray-500 ${isRightAligned ? 'flex-row-reverse' : ''}`}>
              {group.showSenderInfo && (
                <>
                  <span className="font-medium">
                    {isUser ? 'Customer' : isAgent ? (group.messages[0]?.agent_name || 'Agent') : 'AI Assistant'}
                  </span>
                  <span>•</span>
                </>
              )}
              <span>{formatMessageTime(group.endTime)}</span>
            </div>
            
            {isRightAligned && group.showAvatar && (
              <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                {isAgent ? (
                  <User className="w-3 h-3 text-white" />
                ) : (
                  <Bot className="w-3 h-3 text-white" />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

/**
 * Clean message bubble without selection issues
 */
const CleanMessageBubble = React.memo(function CleanMessageBubble({ 
  message, 
  showMetadata,
  messageStatus,
  reactions,
  isFirstInGroup,
  isLastInGroup,
  isRightAligned,
  isNewMessage
}: { 
  message: EnhancedChatMessage
  showMetadata: boolean
  messageStatus?: MessageStatus
  reactions: MessageReaction[]
  isFirstInGroup: boolean
  isLastInGroup: boolean
  isRightAligned: boolean
  isNewMessage: boolean
}) {
  const isUser = message.role === 'user'
  const isAgent = message.role === 'agent' 
  const isAssistant = message.role === 'assistant'
  const isSystem = message.role === 'system'
  const isInternalNote = message.is_internal_note === true
  
  // 🎯 CLEAN PRIVATE NOTE DESIGN - No header, just content
  if (isInternalNote) {
    // 🔍 DEBUG: Log private note rendering
    logger.debug('📝 Rendering private note:', {
      messageId: message.id,
      content: message.content?.substring(0, 30) + '...',
      agentName: message.agent_name,
      isInternalNote: message.is_internal_note
    })
    
    return (
      <div className="flex justify-end mb-4 group message-item">
        <div className="max-w-[75%] min-w-[300px]">
          {/* Modern Note Content - No header */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-sm">
            <div 
              className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{
                __html: formatMessageContent(message.content || 'No content provided')
              }}
              onCopy={(e) => {
                // Clean HTML from copied content
                const selection = window.getSelection()
                if (selection && selection.toString()) {
                  e.preventDefault()
                  const cleanText = cleanContentForCopy(selection.toString())
                  navigator.clipboard.writeText(cleanText)
                }
              }}
            />
            
            {/* Mentions if available */}
            {message.mentioned_agents_info && message.mentioned_agents_info.length > 0 && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <div className="text-xs text-amber-700 mb-2 font-medium">Mentioned:</div>
                <div className="flex flex-wrap gap-1">
                  {message.mentioned_agents_info.map((agent, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-amber-200 text-amber-800"
                    >
                      @{agent.display_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Private note footer */}
            <div className="flex items-center justify-end mt-3 pt-2 border-t border-amber-200">
              <div className="flex items-center gap-1 text-xs text-amber-600">
                <Lock className="w-3 h-3" />
                <span className="font-medium">Private Note</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  // Modern bubble styling - Agent (right) and Customer (left)
  const getBubbleClasses = () => {
    const baseClasses = "px-4 py-3 break-words overflow-wrap-anywhere word-break"
    const colorClasses = isRightAligned 
      ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md" 
      : "bg-white text-gray-800 shadow-md border border-gray-200"
    
    const roundingClasses = "rounded-2xl"
    
    return `${baseClasses} ${colorClasses} ${roundingClasses}`
  }
  
  // Clean bubble container styling
  const bubbleStyle = {
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const,
    outline: 'none'
  }

  return (
    <div style={bubbleStyle} key={message.id}>
      <div className={`flex ${isRightAligned ? 'justify-end' : 'justify-start'}`}>
        <div className={getBubbleClasses()} style={bubbleStyle}>
          {/* System Message Special Styling */}
          {isSystem && (
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-medium text-amber-700">System Message</span>
            </div>
          )}
          
          {/* Message Content */}
          <div 
            className="text-sm leading-relaxed break-words overflow-wrap-anywhere whitespace-pre-wrap"
            dangerouslySetInnerHTML={{
              __html: formatMessageContent(message.content)
            }}
            onCopy={(e) => {
              // Clean HTML from copied content
              const selection = window.getSelection()
              if (selection && selection.toString()) {
                e.preventDefault()
                const cleanText = cleanContentForCopy(selection.toString())
                navigator.clipboard.writeText(cleanText)
              }
            }}
          />
          
          {/* AI Metadata - Only show on last message to avoid clutter */}
          {showMetadata && (isAssistant || isAgent) && isLastInGroup && (
            <div className="mt-1 pt-1">
              <div className="flex flex-wrap gap-1.5">
                {message.confidence_score && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                    isRightAligned 
                      ? 'bg-white bg-opacity-20 text-white' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Target className="w-3 h-3 mr-1" />
                    {Math.round(message.confidence_score * 100)}%
                  </span>
                )}
                {message.sources_used && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                    isRightAligned 
                      ? 'bg-white bg-opacity-20 text-white' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <FileText className="w-3 h-3 mr-1" />
                    {message.sources_used}
                  </span>
                )}
                {message.model_used && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                    isRightAligned
                      ? 'bg-white bg-opacity-20 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Zap className="w-3 h-3 mr-1" />
                    {message.model_used}
                  </span>
                )}
                {message.tool_calls && message.tool_calls.length > 0 && message.tool_calls.map((tc, i) => (
                  <span key={`tool-${i}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                    isRightAligned
                      ? 'bg-white bg-opacity-20 text-white'
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    <Sparkles className="w-3 h-3 mr-1" />
                    {tc.tool}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Message Status - Only show on last message */}
        
        </div>
      </div>
      
      {/* Message Reactions - Show below each message bubble */}
      {reactions.length > 0 && (
        <div className={`flex gap-2 mt-1 ${isRightAligned ? 'justify-end' : 'justify-start'}`}>
          {reactions.map((reaction, index) => (
            <div
              key={index}
              className="flex items-center justify-center w-8 h-8 bg-white rounded-full shadow-sm border border-gray-200 hover:shadow-md transition-all cursor-pointer"
              title={`Customer ${reaction.emoji === '👍' ? 'liked' : 'disliked'} this response`}
            >
              {reaction.emoji === '👍' ? (
                <ThumbsUp className="w-4 h-4 text-green-600" />
              ) : (
                <ThumbsDown className="w-4 h-4 text-red-600" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

/**
 * Typing indicator component
 */
function TypingIndicator({ typingUsers }: { typingUsers: TypingUser[] }) {
  if (typingUsers.length === 0) return null
  
  const typingNames = typingUsers.map(user => user.userName).join(', ')
  
  return (
    <div className="flex justify-start mb-4">
      <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3 border border-gray-200">
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
          <span className="text-sm text-gray-600">
            {typingNames} {typingUsers.length === 1 ? 'is' : 'are'} typing...
          </span>
        </div>
      </div>
    </div>
  )
}


export function RealTimeChatHistory({
  messages,
  conversationId,
  chatbotId,
  showMetadata = true,
  autoScroll = true,
  className = '',
  onMessageSent,
  isLoading = false,
  error = null,
  inputValue: externalInputValue,
  setInputValue: externalSetInputValue,
  isNoteMode: externalIsNoteMode,
  onToggleNoteMode: externalOnToggleNoteMode,
  placeholder: externalPlaceholder,
  sendMessageRef
}: RealTimeChatHistoryProps) {
  
  // 🔍 DEBUG: Log incoming messages to see if is_internal_note is present
  useEffect(() => {
    logger.info('📨 RealTimeChatHistory received messages:', {
      messageCount: messages.length,
      internalNotes: messages.filter(m => m.is_internal_note === true).length,
      sampleMessages: messages.slice(-3).map(m => ({
        id: m.id.substring(0, 8),
        role: m.role,
        is_internal_note: m.is_internal_note,
        content: m.content.substring(0, 30)
      }))
    })

    // Fetch existing reactions for all messages with valid IDs
    const fetchReactionsForMessages = async () => {
      // Filter messages to only include those with valid backend UUIDs
      const validMessages = messages.filter(message => {
        if (!message.id) return false
        
        // Skip temporary optimistic update IDs
        if (message.id.startsWith('temp-')) return false
        
        // Skip WebSocket fallback IDs  
        if (message.id.startsWith('ws-')) return false
        
        // Only fetch reactions for valid UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        return uuidRegex.test(message.id)
      })
      
      logger.debug('🎯 Fetching reactions for valid messages', {
        totalMessages: messages.length,
        validMessages: validMessages.length,
        skippedCount: messages.length - validMessages.length
      })
      
      const reactionPromises = validMessages.map(async (message) => {
        try {
          const response = await api.get(`/api/messages/${message.id}/reactions`)
          if (response.data?.success && response.data?.data?.reactions) {
            return {
              messageId: message.id,
              reactions: response.data.data.reactions.map((r: any) => ({
                messageId: message.id,
                emoji: r.emoji,
                userId: r.users?.[0]?.id || 'unknown',
                userName: r.users?.[0]?.name || 'User',
                timestamp: new Date().toISOString(),
                count: r.count,
                source: r.users?.[0]?.source || 'manual'
              }))
            }
          }
        } catch (error: any) {
          if (error.response?.status === 404) {
            // Message truly not found (invalid ID or permission issue)
            logger.debug('Message not found in backend:', { messageId: message.id })
            return { messageId: message.id, reactions: [] }
          } else if (error.response?.status === 500) {
            logger.warn('Server error fetching reactions for message:', { messageId: message.id, error: error.message })
            return { messageId: message.id, reactions: [] }
          } else {
            logger.warn('Failed to fetch reactions for message:', { messageId: message.id, error: error.message })
            return { messageId: message.id, reactions: [] }
          }
        }
      })

      const results = await Promise.all(reactionPromises)
      const reactionMap: Record<string, MessageReaction[]> = {}
      
      results.forEach(result => {
        if (result.reactions.length > 0) {
          reactionMap[result.messageId] = result.reactions
        }
      })

      setMessageReactions(reactionMap)
      logger.info('📊 Fetched reactions for messages:', { reactionCount: Object.keys(reactionMap).length })
    }

    if (messages.length > 0) {
      fetchReactionsForMessages()
    }
  }, [messages])
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  
  // Real-time state
  const [messageStatuses, setMessageStatuses] = useState<Record<string, MessageStatus>>({})
  const [messageReactions, setMessageReactions] = useState<Record<string, MessageReaction[]>>({})
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([])
  const [isConnected, setIsConnected] = useState(false)
  
  
  // 🎯 Message Source Hierarchy - Clear ownership system
  const [messageStore, setMessageStore] = useState({
    optimistic: new Map<string, EnhancedChatMessage>(), // Agent's pending messages (temp IDs)
    realtime: new Map<string, EnhancedChatMessage>(),   // WebSocket messages (authoritative)
    history: new Map<string, EnhancedChatMessage>(),    // Initial load messages
  })
  
  // 🎯 Crisp-style private note mode (use external state if provided)
  const [internalIsNoteMode, setInternalIsNoteMode] = useState(false)
  // 🔧 FIX: Ensure isNoteMode is always a boolean, not a SyntheticBaseEvent
  const isNoteMode = externalIsNoteMode !== undefined ? Boolean(externalIsNoteMode) : internalIsNoteMode
  const setIsNoteMode = externalIsNoteMode !== undefined ? () => {} : setInternalIsNoteMode
  
  // Input state for modern chat input (use external state if provided)
  const [internalInputValue, setInternalInputValue] = useState('')
  const inputValue = externalInputValue !== undefined ? externalInputValue : internalInputValue
  const setInputValue = externalSetInputValue !== undefined ? externalSetInputValue : setInternalInputValue
  
  // 🎨 Track newly sent messages for animation
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(new Set())
  
  // Get current user for agent name
  const { user } = useAuth()
  
  // 🎯 Memoized callbacks to prevent input remounting (use external callbacks if provided)
  const handleToggleNoteMode = useCallback(() => {
    if (externalOnToggleNoteMode) {
      externalOnToggleNoteMode()
    } else {
      const newNoteMode = !isNoteMode
      setIsNoteMode(newNoteMode)
      logger.info('📝 Note mode toggled', { 
        oldMode: isNoteMode, 
        newMode: newNoteMode 
      })
    }
  }, [isNoteMode, externalOnToggleNoteMode])
  
  const inputPlaceholder = externalPlaceholder || useMemo(() => 
    isNoteMode ? "Add a private note (try @username to mention agents)..." : "Type your message (try @username to mention agents)..."
  , [isNoteMode])
  
  // 🎯 Message Source Hierarchy Helper Functions
  const addOptimisticMessage = useCallback((message: EnhancedChatMessage) => {
    setMessageStore(prev => ({
      ...prev,
      optimistic: new Map(prev.optimistic.set(message.id, message))
    }))
    // Track for animation
    setNewMessageIds(prev => {
      if (!processedMessageIdsRef.current.has(message.id)) {
        processedMessageIdsRef.current.add(message.id)
        return new Set([...prev, message.id])
      }
      return prev
    })
    logger.info('📤 Added optimistic message', { messageId: message.id })
  }, [])
  
  const moveOptimisticToRealtime = useCallback((tempId: string, realMessage: EnhancedChatMessage) => {
    setMessageStore(prev => {
      const newOptimistic = new Map(prev.optimistic)
      const newRealtime = new Map(prev.realtime)
      
      // Check if optimistic message exists
      const optimisticMessage = newOptimistic.get(tempId)
      if (!optimisticMessage) {
        logger.warn('⚠️ Optimistic message not found for replacement', { tempId })
        return prev // No change if optimistic message doesn't exist
      }
      
      // Check if realtime message already exists (prevent duplicates)
      if (newRealtime.has(realMessage.id)) {
        logger.warn('⚠️ Realtime message already exists, skipping replacement', { 
          messageId: realMessage.id,
          tempId 
        })
        return prev // No change if realtime already has this message
      }
      
      // Remove from optimistic and add to realtime
      newOptimistic.delete(tempId)
      newRealtime.set(realMessage.id, realMessage)
      
      logger.info('✅ Moved optimistic to realtime', { 
        tempId, 
        realId: realMessage.id,
        optimisticCount: newOptimistic.size,
        realtimeCount: newRealtime.size
      })
      
      return {
        ...prev,
        optimistic: newOptimistic,
        realtime: newRealtime
      }
    })
    
    // Update animation tracking - remove old, add new
    setNewMessageIds(prev => {
      const newSet = new Set(prev)
      newSet.delete(tempId)
      // Don't add realMessage.id to animations since it's replacing optimistic
      return newSet
    })
  }, [])
  
  const addRealtimeMessage = useCallback((message: EnhancedChatMessage) => {
    setMessageStore(prev => {
      // Check if message already exists in realtime (prevent duplicates)
      if (prev.realtime.has(message.id)) {
        logger.debug('🔄 Realtime message already exists, skipping', { 
          messageId: message.id,
          role: message.role 
        })
        return prev // No change needed
      }
      
      // Check if this message replaces an optimistic one
      const optimisticMatch = Array.from(prev.optimistic.values()).find(opt => 
        opt.content === message.content && 
        opt.role === message.role &&
        Math.abs(new Date(opt.timestamp).getTime() - new Date(message.timestamp).getTime()) < 30000
      )
      
      const newOptimistic = new Map(prev.optimistic)
      const newRealtime = new Map(prev.realtime)
      
      if (optimisticMatch) {
        // Replace optimistic with real message
        newOptimistic.delete(optimisticMatch.id)
        logger.info('🔄 Replacing optimistic with realtime via WebSocket', { 
          optimisticId: optimisticMatch.id, 
          realtimeId: message.id,
          content: message.content.substring(0, 30)
        })
        
        // Remove optimistic from animations, don't add realtime (avoid flash)
        setNewMessageIds(prevIds => {
          const newSet = new Set(prevIds)
          newSet.delete(optimisticMatch.id)
          return newSet
        })
      } else {
        // This is a truly new message from another user/system
        if (message.role === 'user' || message.role === 'assistant' || message.role === 'system') {
          setNewMessageIds(prevIds => {
            if (!processedMessageIdsRef.current.has(message.id)) {
              processedMessageIdsRef.current.add(message.id)
              return new Set([...prevIds, message.id])
            }
            return prevIds
          })
        }
      }
      
      // Add to realtime
      newRealtime.set(message.id, message)
      
      logger.info('📨 Added realtime message', { 
        messageId: message.id, 
        role: message.role,
        wasOptimistic: !!optimisticMatch,
        realtimeCount: newRealtime.size,
        optimisticCount: newOptimistic.size
      })
      
      return {
        ...prev,
        optimistic: newOptimistic,
        realtime: newRealtime
      }
    })
  }, [])
  
  const addHistoryMessages = useCallback((messages: EnhancedChatMessage[]) => {
    setMessageStore(prev => {
      const newHistory = new Map(prev.history)
      let addedCount = 0
      
      messages.forEach(message => {
        // Only add to history if not already in realtime or history
        if (!prev.realtime.has(message.id) && !prev.history.has(message.id)) {
          newHistory.set(message.id, message)
          addedCount++
        }
      })
      
      if (addedCount > 0) {
        logger.info('📚 Added history messages', { count: addedCount })
        return {
          ...prev,
          history: newHistory
        }
      }
      
      return prev // No changes needed
    })
  }, [])
  
  // 🎯 Get final message list with proper priority
  const getFinalMessages = useCallback((): EnhancedChatMessage[] => {
    const { optimistic, realtime, history } = messageStore
    
    // Combine all messages with priority: realtime > optimistic > history
    const allMessages = new Map<string, EnhancedChatMessage>()
    
    // 1. Add history messages first (lowest priority)
    history.forEach((message, id) => {
      allMessages.set(id, message)
    })
    
    // 2. Add optimistic messages (medium priority)
    optimistic.forEach((message, id) => {
      allMessages.set(id, message)
    })
    
    // 3. Add realtime messages (highest priority - overwrites others)
    realtime.forEach((message, id) => {
      allMessages.set(id, message)
    })
    
    // Sort by timestamp
    return Array.from(allMessages.values()).sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
  }, [messageStore])
  
  // 🎯 Source hierarchy system eliminates need for complex deduplication!
  
  // 🎯 Load initial messages from props into history store
  useEffect(() => {
    if (messages.length > 0) {
      // Only load history if we don't have realtime messages yet
      setMessageStore(prev => {
        const hasRealtimeMessages = prev.realtime.size > 0
        if (hasRealtimeMessages) {
          logger.info('📚 Skipping history load - have realtime messages')
          return prev
        }
        
        const newHistory = new Map(prev.history)
        messages.forEach(message => {
          newHistory.set(message.id, message)
        })
        
        logger.info('📚 Loaded initial conversation history', { count: messages.length })
        return {
          ...prev,
          history: newHistory
        }
      })
    }
  }, [messages])
  
  // Get final messages using source hierarchy
  const finalMessages = getFinalMessages()
  
  // Group messages using the clean source hierarchy system with memoization
  const messageGroups = useMemo(() => {
    return groupMessages(finalMessages)
  }, [finalMessages])
  
  // WhatsApp-style auto-scroll: instant scroll to bottom without animation
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      // Use requestAnimationFrame for better timing and performance
      requestAnimationFrame(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView(false) // false = instant, no smooth scrolling
        }
      })
    }
  }, [finalMessages.length, autoScroll])
  
  // 🎨 Cleanup animation tracking after 1 second to prevent conflicts
  useEffect(() => {
    if (newMessageIds.size > 0) {
      const cleanup = setTimeout(() => {
        setNewMessageIds(new Set())
        // Also clear the processed IDs to allow future animations
        processedMessageIdsRef.current.clear()
      }, 1000) // Clear after 1 second (faster cleanup)
      
      return () => clearTimeout(cleanup)
    }
  }, [newMessageIds])
  
  // 🎨 Prevent duplicate animations by tracking processed messages
  const processedMessageIdsRef = useRef<Set<string>>(new Set())
  
  // 🎯 DEBOUNCE: Create debounced state updaters to prevent rapid updates that cause "refreshing"
  const debouncedMessageStoreUpdate = useCallback((updateFn: (prev: any) => any) => {
    const timeoutId = setTimeout(() => {
      setMessageStore(updateFn)
    }, 50) // Very short debounce for immediate feel but prevents rapid duplicates
    
    return () => clearTimeout(timeoutId)
  }, [])
  
  const debouncedReactionUpdate = useCallback((updateFn: (prev: any) => any) => {
    const timeoutId = setTimeout(() => {
      setMessageReactions(updateFn)
    }, 100) // Slightly longer for reactions since they're less critical
    
    return () => clearTimeout(timeoutId)
  }, [])
  
  // Setup real-time WebSocket connection
  useEffect(() => {
    logger.info('🔌 Setting up WebSocket connection for conversation', { conversationId })
    
    // Connect to conversation WebSocket
    websocketService.connectToConversation(conversationId)
    
    // Subscribe to WebSocket messages with debounced updates
    const unsubscribe = websocketService.subscribe(conversationId, (message: WebSocketMessage) => {
      // 🎯 Reduce logging noise - only log important message types
      if (['message_received', 'message_reaction_added', 'message_reaction_removed', 'customer_ended_chat'].includes(message.type)) {
        logger.info('📨 WebSocket message received:', message.type, message.conversation_id || conversationId)
      }
      
      switch (message.type) {
        case 'connected':
          setIsConnected(true)
          logger.info('💬 Real-time chat connected')
          break
          
        case 'message_received':
          // New message received - add to realtime store with source hierarchy
          logger.info('📨 New message received via WebSocket')
          
          if (message.data) {
            // Validate that backend provides proper message ID
            if (!message.data.id) {
              logger.warn('⚠️ WebSocket message missing ID - backend should provide proper UUID', {
                messageData: message.data,
                conversationId
              })
            }
            
            const newMessage: EnhancedChatMessage = {
              id: message.data.id || `ws-${Date.now()}-${Math.random()}`,
              content: message.data.content || '',
              role: message.data.role || 'user',
              timestamp: message.data.timestamp || new Date().toISOString(),
              confidence_score: message.data.confidence_score,
              sources_used: message.data.sources_used,
              processing_time_ms: message.data.processing_time_ms,
              model_used: message.data.model_used,
              tool_calls: message.data.tool_calls || undefined,
              // 🎯 Add Crisp-style note fields
              agent_name: message.data.agent_name,
              is_internal_note: message.data.is_internal_note || false,
              mentioned_agents: message.data.mentioned_agents || [],
              mentioned_agents_info: message.data.mentioned_agents_info || [],
              mention_type: message.data.mention_type
            }
            
            // 🔍 DEBUG: Log agent name being received
            logger.info('🔍 DEBUG: WebSocket message agent_name received:', {
              agent_name: message.data.agent_name,
              agent_id: message.data.agent_id,
              is_internal_note: message.data.is_internal_note
            })
            
            // 🎯 Use new source hierarchy system
            // Only add messages from others (not our own agent messages - those are handled optimistically)
            // 🔧 FIX: Handle private notes carefully to avoid duplicates and rapid updates
            if (newMessage.role === 'user' || newMessage.role === 'system' || newMessage.role === 'visitor' || newMessage.role === 'assistant') {
              // 🎯 Use debounced update for smoother experience
              debouncedMessageStoreUpdate(prev => {
                const newRealtime = new Map(prev.realtime)
                newRealtime.set(newMessage.id, newMessage)
                logger.info('📨 Added realtime message (debounced):', {
                  messageId: newMessage.id.substring(0, 8),
                  role: newMessage.role,
                  isInternalNote: newMessage.is_internal_note
                })
                return { ...prev, realtime: newRealtime }
              })
            } else if (newMessage.role === 'agent' && newMessage.is_internal_note) {
              // For private notes from agents, check if this is our own note coming back via WebSocket
              const isOwnNote = newMessage.agent_name === user?.full_name || newMessage.agent_name === user?.name
              
              if (isOwnNote) {
                logger.debug('📝 Own private note received via WebSocket - skipping to prevent duplicates:', {
                  messageId: newMessage.id.substring(0, 8),
                  agentName: newMessage.agent_name
                })
                // Don't add as realtime - let the optimistic->realtime flow handle it
              } else {
                // Private note from another agent - add it with debouncing
                debouncedMessageStoreUpdate(prev => {
                  const newRealtime = new Map(prev.realtime)
                  newRealtime.set(newMessage.id, newMessage)
                  logger.info('📝 Private note from other agent added (debounced):', {
                    messageId: newMessage.id.substring(0, 8),
                    agentName: newMessage.agent_name
                  })
                  return { ...prev, realtime: newRealtime }
                })
              }
            } else {
              logger.debug('🔄 Skipping agent message from WebSocket (handled optimistically)', {
                messageId: newMessage.id.substring(0, 8),
                role: newMessage.role,
                isInternalNote: newMessage.is_internal_note
              })
            }
          }
          break
          
        case 'user_connected':
        case 'user_disconnected':
          logger.info(`👤 User ${message.type.replace('user_', '')}:`, message.user_name)
          break
          
        case 'typing_update':
          if (message.typing_users) {
            setTypingUsers(message.typing_users)
          }
          break
          
        case 'heartbeat':
          // Keep connection alive
          break
          
        case 'message_reaction_added':
          // Real-time reaction addition with debouncing
          logger.info('😄 Reaction added via WebSocket:', message.data)
          if (message.data && message.data.message_id) {
            // 🎯 Use debounced update to prevent rapid state changes
            debouncedReactionUpdate(prev => {
              const updated = { ...prev }
              const messageId = message.data.message_id
              
              // Initialize reactions array if it doesn't exist  
              if (!updated[messageId]) {
                updated[messageId] = []
              }
              
              // Force new array reference to trigger React re-render
              updated[messageId] = [...updated[messageId]]
              
              // Find existing reaction with same emoji
              const existingReactionIndex = updated[messageId].findIndex(r => r.emoji === message.data.emoji)
              
              if (existingReactionIndex >= 0) {
                // Update existing reaction count and add user
                const existingReaction = updated[messageId][existingReactionIndex]
                const userExists = existingReaction.users.some(u => u.id === message.data.user_id)
                
                if (!userExists) {
                  updated[messageId][existingReactionIndex] = {
                    ...existingReaction,
                    count: existingReaction.count + 1,
                    users: [...existingReaction.users, {
                      id: message.data.user_id,
                      name: message.data.user_name,
                      role: 'user'
                    }]
                  }
                }
              } else {
                // Add new reaction
                updated[messageId].push({
                  emoji: message.data.emoji,
                  count: 1,
                  users: [{
                    id: message.data.user_id,
                    name: message.data.user_name,
                    role: 'user'
                  }],
                  has_reacted: false
                })
              }
              
              logger.info('✅ Updated reactions in real-time', {
                messageId,
                emoji: message.data.emoji,
                totalReactions: updated[messageId].length,
                reactionData: updated[messageId],
                stateChange: JSON.stringify(updated[messageId]) !== JSON.stringify(prev[messageId])
              })
              
              return updated
            })
          }
          break
          
        case 'message_reaction_removed':
          // Real-time reaction removal with debouncing
          logger.info('🗑️ Reaction removed via WebSocket:', message.data)
          if (message.data && message.data.message_id) {
            // 🎯 Use debounced update to prevent rapid state changes
            debouncedReactionUpdate(prev => {
              const updated = { ...prev }
              const messageId = message.data.message_id
              
              if (updated[messageId]) {
                // Force new array reference to trigger React re-render
                updated[messageId] = [...updated[messageId]]
                
                // Find and update/remove reaction
                const reactionIndex = updated[messageId].findIndex(r => r.emoji === message.data.emoji)
                
                if (reactionIndex >= 0) {
                  const reaction = updated[messageId][reactionIndex]
                  const userIndex = reaction.users.findIndex(u => u.id === message.data.user_id)
                  
                  if (userIndex >= 0) {
                    // Remove user from reaction
                    const updatedUsers = reaction.users.filter(u => u.id !== message.data.user_id)
                    
                    if (updatedUsers.length === 0) {
                      // Remove reaction entirely if no users left
                      updated[messageId] = updated[messageId].filter((_, index) => index !== reactionIndex)
                    } else {
                      // Update reaction with remaining users
                      updated[messageId][reactionIndex] = {
                        ...reaction,
                        count: updatedUsers.length,
                        users: updatedUsers
                      }
                    }
                  }
                }
              }
              
              logger.info('✅ Removed reaction in real-time', {
                messageId,
                emoji: message.data.emoji,
                remainingReactions: updated[messageId]?.length || 0,
                reactionData: updated[messageId],
                stateChange: JSON.stringify(updated[messageId]) !== JSON.stringify(prev[messageId])
              })
              
              return updated
            })
          }
          break
          
        case 'customer_ended_chat':
          // Customer ended chat session
          logger.info('🔚 Customer ended chat via WebSocket:', message.data)
          if (message.data) {
            // Show system message about customer ending chat
            const systemMessage = {
              id: `system_${Date.now()}`,
              role: 'system' as const,
              content: `${message.data.visitor_name || 'Customer'} ended the chat session${message.data.auto_resolved ? ' and conversation was automatically resolved' : ''}`,
              timestamp: message.data.ended_at || new Date().toISOString(),
              is_internal_note: false,
              metadata: {
                type: 'customer_ended_chat',
                session_id: message.data.session_id,
                session_duration: message.data.session_duration,
                conversation_status: message.data.conversation_status,
                auto_resolved: message.data.auto_resolved
              }
            }
            
            // Add system message to real-time store
            addRealtimeMessage(systemMessage)
          }
          break
          
        case 'widget_visitor_disconnected':
          // Customer disconnected from widget
          logger.info('🔌 Customer disconnected via WebSocket:', message)
          if (message.visitor_name || message.session_id) {
            // Show system message about customer disconnecting
            const systemMessage = {
              id: `system_${Date.now()}`,
              role: 'system' as const,
              content: `${message.visitor_name || 'Customer'} left the chat session`,
              timestamp: message.timestamp || new Date().toISOString(),
              is_internal_note: false,
              metadata: {
                type: 'widget_visitor_disconnected',
                session_id: message.session_id,
                visitor_name: message.visitor_name
              }
            }
            
            // Add system message to real-time store
            addRealtimeMessage(systemMessage)
          }
          break
          
        default:
          logger.debug('🔍 Unknown WebSocket message type:', message.type)
      }
    })
    
    // Cleanup on unmount
    return () => {
      logger.info('🧹 Cleaning up WebSocket connection')
      unsubscribe()
      websocketService.disconnectFromConversation(conversationId)
      setIsConnected(false)
    }
  }, [conversationId, debouncedMessageStoreUpdate, debouncedReactionUpdate, user])
  
  // Legacy message update handler (keeping for potential future use)
  const handleMessageUpdate = (data: any) => {
    logger.info('📨 Message update received:', data)
    // Update message status if provided
    if (data.status) {
      setMessageStatuses(prev => ({
        ...prev,
        [data.message_id]: {
          messageId: data.message_id,
          status: data.status,
          timestamp: data.timestamp
        }
      }))
    }
  }
  

  // Handle sending new messages (Agent Reply) - WhatsApp-style optimistic updates
  const handleSendMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim()) return
    
    // 🎯 Detect @mentions in message content (Crisp-style)
    const mentionPattern = /@(\w+)/g
    const mentions: string[] = []
    let match
    while ((match = mentionPattern.exec(messageText)) !== null) {
      mentions.push(match[1])
    }
    
    const hasNoteCommand = messageText.startsWith('/note') || messageText.startsWith('/')
    const isPrivateNote = isNoteMode || mentions.length > 0 || hasNoteCommand
    
    logger.info(`👨‍💼 Sending agent message: ${messageText}`, {
      isPrivateNote,
      hasMentions: mentions.length > 0,
      mentionCount: mentions.length,
      noteMode: isNoteMode,
      hasNoteCommand,
      messageText: messageText
    })
    
    // 🎯 Create optimistic message for instant feedback
    const optimisticMessageId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const optimisticMessage: EnhancedChatMessage = {
      id: optimisticMessageId,
      content: messageText.trim(),
      role: 'agent',
      timestamp: new Date().toISOString(),
      confidence_score: undefined,
      sources_used: undefined,
      processing_time_ms: undefined,
      model_used: undefined,
      agent_name: user?.full_name || 'Agent',
      is_internal_note: isPrivateNote,
      mentioned_agents: mentions,
      mentioned_agents_info: [], // Will be populated by WebSocket response
      mention_type: mentions.length > 0 ? 'direct' : (isPrivateNote ? 'note' : null)
    }
    
    // 🎯 Add to optimistic store for instant feedback
    addOptimisticMessage(optimisticMessage)
    
    // 🔍 DEBUG: Log optimistic private note creation
    if (isPrivateNote) {
      logger.info('📝 Added optimistic private note:', {
        messageId: optimisticMessageId,
        content: messageText.substring(0, 50) + '...',
        isInternalNote: optimisticMessage.is_internal_note,
        noteMode: isNoteMode
      })
    }
    
    // 🎯 Clear input immediately for better UX (WhatsApp-style)
    setInputValue('')
    
    try {
      // 📤 Send message to backend
      // 🎯 CRITICAL FIX: Prepend /note command when in note mode so backend knows it's private
      // Save note mode state before we reset it
      const wasInNoteMode = isNoteMode
      let messageToSend = messageText.trim()
      if (wasInNoteMode && !messageText.startsWith('/note')) {
        messageToSend = `/note ${messageText.trim()}`
        logger.info('🔒 Adding /note command to message for backend processing', { 
          originalMessage: messageText,
          finalMessage: messageToSend,
          wasInNoteMode 
        })
      }
      
      // 🎯 Reset note mode after preparing message but before sending (Crisp behavior)
      if (isPrivateNote) {
        setIsNoteMode(false)
      }
      
      const response = await api.post(`/live-chat/conversations/${conversationId}/agent-reply`, {
        conversation_id: conversationId,
        message: messageToSend,
        agent_id: null
      })
      
      logger.info('✅ Agent reply sent successfully:', response.data)
      
      // Move optimistic message to realtime with real data
      if (response.data?.message_id) {
        const realMessage: EnhancedChatMessage = {
          ...optimisticMessage,
          id: response.data.message_id,
          // Use backend timestamp instead of frontend-generated one
          timestamp: response.data.timestamp || optimisticMessage.timestamp,
          // Add any additional data from response
          mentioned_agents_info: response.data.mentioned_agents_info || optimisticMessage.mentioned_agents_info
        }
        
        moveOptimisticToRealtime(optimisticMessageId, realMessage)
      }
      
      // 🎯 No need to trigger parent refresh - WebSocket handles real-time updates
      logger.info('✅ Message sent successfully - WebSocket will handle real-time updates')
      
      // 🔧 FIX: Don't call onMessageSent for private notes to prevent unnecessary refreshes
      // Private notes are handled entirely via WebSocket and optimistic updates
      if (onMessageSent && !isPrivateNote) {
        onMessageSent()
        logger.info('💬 Message sent, refreshing conversation data')
      } else if (isPrivateNote) {
        logger.info('📝 Private note sent, skipping refresh to prevent reloading all notes')
      }
      
    } catch (error) {
      logger.error('❌ Failed to send agent reply:', error)
      
      // Remove the failed optimistic message
      setMessageStore(prev => {
        const newOptimistic = new Map(prev.optimistic)
        newOptimistic.delete(optimisticMessageId)
        return {
          ...prev,
          optimistic: newOptimistic
        }
      })
      
      // Remove from animation tracking
      setNewMessageIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(optimisticMessageId)
        return newSet
      })
      
      logger.info('🗑️ Removed failed optimistic message', { messageId: optimisticMessageId })
      
      // 🎯 Restore input value on failure so user can retry
      setInputValue(messageText)
      
      alert('Failed to send agent reply. Please try again.')
    }
  }, [conversationId, user, isNoteMode, addOptimisticMessage, moveOptimisticToRealtime, setMessageStore, onMessageSent])
  
  // Expose handleSendMessage to parent component via ref
  useEffect(() => {
    if (sendMessageRef) {
      sendMessageRef.current = handleSendMessage
    }
  }, [handleSendMessage, sendMessageRef])
  
  // Handle error state
  if (error) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center text-red-500">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-red-300" />
          <p className="text-sm">Error loading conversation</p>
          <p className="text-xs text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    )
  }
  
  // Handle loading state
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center text-gray-500">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm">Loading conversation...</p>
        </div>
      </div>
    )
  }
  
  if (finalMessages.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center text-gray-500">
          <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No messages in this conversation yet</p>
          {isConnected && (
            <div className="mt-2 flex items-center justify-center gap-2 text-xs text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Live updates active</span>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  return (
    <div 
      ref={containerRef}
      className={`h-full flex flex-col bg-gradient-to-b from-gray-50 to-white ${className}`}
    >
      {/* 🟢 TOP STATUS BAR */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Customer Avatar & Info */}
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">AC</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Anonymous Customer</h3>
              <p className="text-sm text-gray-500">Conversation ID: {conversationId.slice(-8)}</p>
            </div>
          </div>
          
          {/* Additional Actions */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 💬 MESSAGES AREA */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-2 pb-4 space-y-1">
        
        {/* Messages - Grouped for modern WhatsApp/iMessage style */}
        {messageGroups.map((group) => {
          // Create a stable key that changes only when the group content changes
          // This helps React avoid unnecessary re-renders
          const groupKey = `${group.id}-${group.messages.length}-${group.messages[group.messages.length-1]?.id || 'empty'}`
          
          return (
            <MessageGroupComponent
              key={groupKey}
              group={group}
              showMetadata={showMetadata}
              messageStatuses={messageStatuses}
              messageReactions={messageReactions}
              newMessageIds={newMessageIds}
            />
          )
        })}
        
        {/* Typing Indicator */}
        <TypingIndicator typingUsers={typingUsers} />
        
        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
      
      {/* 📝 CHAT INPUT */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white">
        <ModernChatInput
          value={externalInputValue || inputValue}
          onChange={externalSetInputValue || setInputValue}
          onSend={handleSendMessage}
          placeholder={externalPlaceholder || inputPlaceholder}
          isNoteMode={isNoteMode}
          onToggleNoteMode={externalOnToggleNoteMode || handleToggleNoteMode}
          disabled={!user || isLoading}
        />
      </div>
      
    </div>
  )
}

// Memoize the component to prevent re-renders when parent updates
// Only re-render when critical props change
export default React.memo(RealTimeChatHistory, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  // We want to re-render only when these specific props change:
  return (
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.messages === nextProps.messages &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.error === nextProps.error &&
    prevProps.inputValue === nextProps.inputValue &&
    prevProps.isNoteMode === nextProps.isNoteMode &&
    prevProps.chatbotId === nextProps.chatbotId
  )
})